import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  fetchUserReturns,
  fetchUserReturnDetail,
  markReturnShipped,
  resetReturnState,
  resetCurrentReturn,
} from "../store/slices/returnsUserSlice";

/**
 * Hook for managing user returns functionality
 * @returns {Object} Returns management methods and state
 */
const useUserReturns = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("hooks");

  // Select state from Redux store with memoized selectors
  const returns = useSelector((state) => state.userReturns?.returns || []);
  const pagination = useSelector(
    (state) => state.userReturns?.pagination || null
  );
  const status = useSelector((state) => state.userReturns?.status || "idle");
  const error = useSelector((state) => state.userReturns?.error || null);
  const currentReturn = useSelector(
    (state) => state.userReturns?.currentReturn || null
  );
  const actionStatus = useSelector(
    (state) => state.userReturns?.actionStatus || "idle"
  );
  const actionError = useSelector(
    (state) => state.userReturns?.actionError || null
  );

  // State for pagination and filtering, managed by the hook
  const [currentPage, setCurrentPage] = useState(1); // 1-based for API
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState(""); // Renamed setter for clarity
  const [searchTerm, setSearchTerm] = useState(""); // Renamed setter for clarity

  // State for shipping form
  const [trackingInfo, setTrackingInfo] = useState({
    returnId: "",
    trackingNumber: "",
    carrier: "",
    shippedDate: new Date().toISOString().split("T")[0], // Default to today
  });

  /**
   * Load returns data based on pagination and filter state
   */
  const loadReturns = useCallback(() => {
    // Prepare query parameters
    const params = {
      page: currentPage,
      limit: rowsPerPage,
    };

    // Only add status filter if it's not empty
    if (statusFilter) {
      params.status = statusFilter;
    }

    // Only add search term if it's not empty
    if (searchTerm) {
      params.search = searchTerm;
    }

    dispatch(fetchUserReturns(params));
  }, [dispatch, currentPage, rowsPerPage, statusFilter, searchTerm]);

  /**
   * Load returns when dependencies change
   */
  useEffect(() => {
    loadReturns();
  }, [loadReturns]);

  /**
   * Handle page change
   * @param {Event} event - Page change event
   * @param {number} newPage - New page number
   */
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage + 1); // Convert from 0-indexed UI to 1-indexed API
  };

  /**
   * Handle rows per page change
   * @param {Event} event - Rows per page change event
   */
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  // Getter for totalReturns, derived from pagination object
  const totalReturns = pagination?.totalItems || 0;

  /**
   * Navigate to return detail page
   * @param {string} returnId - Return ID
   */
  const viewReturnDetail = (returnId) => {
    navigate(`/account/returns/${returnId}`);
  };

  /**
   * Load single return detail
   * @param {string} returnId - Return ID to load
   */
  const loadReturnDetail = useCallback(
    (returnId) => {
      if (!returnId) {
        console.error("Cannot load return detail: returnId is undefined");
        return () => {};
      }

      dispatch(fetchUserReturnDetail(returnId));

      return () => {
        dispatch(resetCurrentReturn());
      };
    },
    [dispatch]
  );

  /**
   * Handle tracking info form change
   * @param {Event} e - Input change event
   */
  const handleTrackingInfoChange = (e) => {
    const { name, value } = e.target;
    setTrackingInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Mark return as shipped
   * @param {Object} params - Parameters object
   * @param {string} params.returnId - Return ID to mark as shipped
   * @param {Object} params.trackingInfo - Tracking information
   */
  const handleMarkAsShipped = useCallback(
    async (params = {}) => {
      // Use provided params or fall back to hook's internal state
      const finalReturnId = params.returnId || trackingInfo.returnId;
      const finalTrackingInfo = params.trackingInfo || {
        trackingNumber: trackingInfo.trackingNumber,
        shippingCarrier: trackingInfo.carrier,
      };

      // Validate required fields
      if (!finalReturnId || !finalTrackingInfo.trackingNumber) {
        toast.error(t("userReturns.trackingRequired"));
        return false;
      }

      try {
        await dispatch(
          markReturnShipped({
            returnId: finalReturnId,
            trackingInfo: finalTrackingInfo,
          })
        ).unwrap();
        toast.success(t("userReturns.shippedSuccess"));

        // Reload returns data after successful shipping
        const reloadParams = {
          page: currentPage,
          limit: rowsPerPage,
        };
        if (statusFilter) {
          reloadParams.status = statusFilter;
        }
        if (searchTerm) {
          reloadParams.search = searchTerm;
        }
        dispatch(fetchUserReturns(reloadParams));

        return true;
      } catch (err) {
        toast.error(err || t("userReturns.shippedError"));
        return false;
      }
    },
    [
      dispatch,
      trackingInfo,
      t,
      currentPage,
      rowsPerPage,
      statusFilter,
      searchTerm,
    ]
  );

  /**
   * Set return ID for tracking info
   * @param {string} returnId - Return ID
   */
  const setReturnIdForTracking = (returnId) => {
    setTrackingInfo((prev) => ({
      ...prev,
      returnId,
    }));
  };

  return {
    // Data
    returns,
    currentReturn,
    totalReturns,

    // Status & Error
    status,
    error,
    actionStatus,
    actionError,

    // Pagination & Filter State
    currentPage: currentPage - 1, // Convert to 0-indexed for UI
    rowsPerPage,
    statusFilter,
    searchTerm,

    // Setters
    setCurrentPage: handlePageChange, // Expects 0-indexed page from UI
    setRowsPerPage: handleRowsPerPageChange,
    setStatusFilter,
    setSearchTerm,

    // Tracking info
    trackingInfo,
    handleTrackingInfoChange,
    setReturnIdForTracking,

    // Actions
    loadReturns,
    viewReturnDetail,
    loadReturnDetail,
    handleMarkAsShipped,
  };
};

export default useUserReturns;
